import { z } from "zod";
import type { ChatChunk, ImageReference, PersonaImageInfo, PersonaImageCollection } from ".";

/**
 * Represents a choice option that can be presented to the user
 */
export interface ChoiceOptionData {
  id?: string;
  name: string;
  description?: string;
  imageKey?: string; // Reference to persona image like "B11"
}

/**
 * Choice data structure that can come from streaming
 */
export interface ChoiceData {
  question?: string;
  options: ChoiceOptionData[];
  mode?: "grid" | "list";
  showTextInput?: boolean;
}

/**
 * One pose entry inside a section's PoseScript. The AI emits these as a JSON
 * array inside a fenced ```PoseScript``` code block per content section.
 *
 * - `pose` may be either a panel variation ID (e.g. "N11") or a well-known
 *   pose category (e.g. "thinking"). The frontend resolves it via the
 *   PersonaImageCollection.
 * - `description` is a short hint about the AI's intent. It is not rendered;
 *   it just helps the AI plan and helps us debug.
 */
export const poseScriptEntrySchema = z.object({
  pose: z.string().min(1),
  description: z.string().optional(),
  /**
   * Optional hold duration in milliseconds. When present, the editor uses
   * it as the imported step's duration; when absent, a sensible default
   * is synthesized at import time. Bounded to keep the AI in a useful
   * range — too short reads as a flicker, too long reads as a stall.
   */
  durationMs: z.number().int().min(50).max(8000).optional(),
});

export const poseScriptSchema = z.array(poseScriptEntrySchema);

export type PoseScriptEntry = z.infer<typeof poseScriptEntrySchema>;
export type PoseScript = z.infer<typeof poseScriptSchema>;

/**
 * On-disk capture of a single AI-produced chat script.
 *
 * Persisted as a JSON file the user downloads from the chat panel.
 * Re-imported by the animation editor (test/chat/editor) to reconstruct
 * one or more {@link NamedPoseSequence}s for editing/playback.
 *
 * The shape is intentionally self-contained and free of runtime
 * references (no `PersonaImageInfo`, no Firestore refs) so the file
 * round-trips cleanly between machines, gets diff-friendly in git, and
 * can be hand-authored as a fixture if needed.
 */
export const savedChatScriptSchema = z.object({
  version: z.literal(1),
  presentation: z.literal("studio-fall").optional(),
  savedAt: z.string(),
  source: z.object({
    personaName: z.string().optional(),
    personaId: z.string().optional(),
    animalName: z.string().optional(),
    animalId: z.string().optional(),
    userMessage: z.string().optional(),
  }),
  sections: z.array(
    z.object({
      text: z.string(),
      poseScript: poseScriptSchema.optional(),
      /** When set, timed players auto-advance after this many ms (prep/close omit). */
      durationMs: z.number().int().min(0).max(600_000).optional(),
    })
  ),
});

export type SavedChatScript = z.infer<typeof savedChatScriptSchema>;

export interface Section {
  text: string;
  characterImage?: PersonaImageInfo | null;
  /**
   * Ordered list of poses the AI scripted for this section. Populated when
   * the user enabled the `produceScript` intent and the AI emitted a valid
   * ```PoseScript``` fenced code block.
   */
  poseScript?: PoseScript;
  /** Timed auto-advance for buddy sessions (ms). Omit for tap-to-advance steps. */
  durationMs?: number;
}

export interface LoomState {
  // Thinking state
  ideas: string[];
  thinkingStatus: string;
  isGeneratingImage: boolean;
  latestGeneratedImage: ImageReference | null;

  // Content state
  streamingText: string;
  streamingSections: Section[];
  imageVariation: string | null;

  // Choice state (separate from section-embedded choices)
  activeChoices: ChoiceData | null;
}

export type LoomListener = (state: LoomState) => void;

/**
 * Core streaming content assembly engine.
 *
 * Receives ChatChunk data, assembles text content, tracks images/status/choices,
 * parses sections, and notifies listeners on state changes.
 *
 * Has no dependencies on React or any UI framework.
 */
export class Loom {
  private _state: LoomState = createInitialState();
  private _listeners: Set<LoomListener> = new Set();
  private _personaImages: PersonaImageCollection | undefined;
  private _sectionsDirty = true;
  /**
   * Pose scripts received via `event: "DATA"` chunks of type "PoseScript",
   * preserved in stream order. Zipped onto sections (by index) on each
   * recomputation, mirroring how the AI emits one block per section.
   */
  private _poseScripts: PoseScript[] = [];

  constructor(personaImages?: PersonaImageCollection) {
    this._personaImages = personaImages;
  }

  /** Returns current state, lazily recomputing sections if dirty. */
  get state(): LoomState {
    if (this._sectionsDirty) {
      const sections = parseStreamingContent(
        this._state.streamingText,
        this._personaImages,
        this._state.activeChoices
      );
      // Zip each PoseScript onto its corresponding section. The AI emits
      // one block per content section in order, so positional zipping is
      // sufficient and avoids needing the AI to track section indices.
      for (let i = 0; i < sections.length; i++) {
        if (this._poseScripts[i]) {
          sections[i].poseScript = this._poseScripts[i];
        }
      }
      this._state.streamingSections = sections;
      this._sectionsDirty = false;
    }
    return this._state;
  }

  setPersonaImages(images: PersonaImageCollection | undefined): void {
    this._personaImages = images;
    this._sectionsDirty = true;
    this.notify();
  }

  processChunk(chunk: ChatChunk): void {
    if (!chunk) return;

    let changed = false;

    // Accumulate streaming text
    if ("text" in chunk && chunk.text) {
      this._state.streamingText += chunk.text;
      this._sectionsDirty = true;
      changed = true;
    }

    // Process status
    if ("status" in chunk && chunk.status) {
      this._state.thinkingStatus = chunk.status;
      changed = true;
    }

    // Process IMAGE_GEN event
    if ("event" in chunk && chunk.event === "IMAGE_GEN") {
      this._state.isGeneratingImage = true;
      this._state.latestGeneratedImage = null;
      changed = true;
    }

    // Process image
    if ("image" in chunk) {
      this._state.latestGeneratedImage = chunk.image;
      this._state.isGeneratingImage = false;
      changed = true;
    }

    // Process imageVariation
    if ("imageVariation" in chunk && chunk.imageVariation) {
      this._state.imageVariation = chunk.imageVariation;
      changed = true;
    }

    // Process ideas
    if ("ideas" in chunk && chunk.ideas) {
      this._state.ideas = chunk.ideas;
      changed = true;
    }

    // Process PoseScript data chunks (one per content section, in order).
    // The backend already validated the JSON via the ProduceScript intent;
    // we re-validate defensively here so a bad payload can't poison state.
    if ("data" in chunk && chunk.data?.type === "PoseScript") {
      const parsed = poseScriptSchema.safeParse(chunk.data.content);
      if (parsed.success) {
        this._poseScripts.push(parsed.data);
        this._sectionsDirty = true;
        changed = true;
      } else {
        // eslint-disable-next-line no-console
        console.warn("[Loom] PoseScript rejected", parsed.error.issues);
      }
    }

    // Process Question data chunks
    if ("data" in chunk && chunk.data?.type === "Question" && chunk.data.content) {
      const questionData = chunk.data.content;

      const choiceData: ChoiceData = {
        question: questionData.question,
        options: (questionData.choices || []).map((choice: any) => {
          let imageKey: string | undefined;
          if (choice.expressionToken) {
            const match = choice.expressionToken.match(/@([A-Z]\d+)/);
            if (match) {
              imageKey = match[1];
            }
          }

          return {
            id: choice.id,
            name: choice.name,
            description: choice.description,
            imageKey: imageKey,
          };
        }),
        showTextInput: questionData.freeform || false,
        mode: "grid",
      };

      this._state.activeChoices = choiceData;
      this._sectionsDirty = true;
      changed = true;
    }

    if (changed) {
      this.notify();
    }
  }

  reset(): void {
    this._state = createInitialState();
    this._poseScripts = [];
    this._sectionsDirty = true;
    this.notify();
  }

  subscribe(listener: LoomListener): () => void {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }

  private notify(): void {
    if (this._listeners.size === 0) return;
    // Recompute sections lazily only when listeners exist and will read state
    const snapshot = { ...this.state };
    for (const listener of this._listeners) {
      listener(snapshot);
    }
  }
}

/**
 * Parses streaming text by horizontal lines (---, ***, or ___)
 * Returns an array of sections, or a single-item array with the original text if no separators found.
 * Also detects and parses choice blocks within sections.
 */
function parseStreamingContent(
  text: string | undefined,
  personaImages: PersonaImageCollection | undefined,
  activeChoices: ChoiceData | null
): Section[] {
  if (!text) return [];

  // Split by horizontal line markers (---, ***, or ___), handling various formats
  const sections: Section[] = text
    .split(/\n\s*[-*_]{3,}\s*\n/)
    .map((section) => section.trim())
    .filter((section) => {
      return section.length > 0 && /\S/.test(section);
    })
    .map((sectionText) => {
      return { text: sectionText };
    });

  // Find the first pattern like @A9, @A13, @A123, etc. in the sections
  const pattern = /`?(@([A-Z]\d+))`?/;

  for (const section of sections) {
    const match = section.text.match(pattern);
    if (match) {
      section.characterImage = personaImages?.lookup(match[2]) ?? undefined;
    }
  }

  return sections;
}

function createInitialState(): LoomState {
  return {
    ideas: [],
    thinkingStatus: "",
    isGeneratingImage: false,
    latestGeneratedImage: null,
    streamingText: "",
    streamingSections: [],
    imageVariation: null,
    activeChoices: null,
  };
}
